import { useCallback } from 'react';
import {
  Datatable,
  DatatableBody,
  DatatableCell,
  DatatableHead,
  DatatableHeader,
  DatatableRow,
  Skeleton,
} from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import { SortableHead } from '@/shared/ui/data-table';
import { EmptyState } from '@/shared/ui/EmptyState';
import { formatRelativeDate } from '@/shared/utils/dateUtils';
import type { GuidelineResponseDto } from '@/lib/api/guidelines-client';
import { CategoryBadgeCell } from './CategoryBadgeCell';
import { CategoryFilterHead } from './CategoryFilterHead';
import { GuidelineDescriptionCell } from './GuidelineDescriptionCell';
import { GuidelineRowActions } from './GuidelineRowActions';
import { GUIDELINES_COLUMNS } from './GuidelinesDatatable.constants';
import type { GuidelinesDatatableProps, SortableGuidelineKeys } from './GuidelinesDatatable.types';

const SKELETON_ROWS = 10;
const COLUMN_COUNT = GUIDELINES_COLUMNS.length + 1;

export function GuidelinesDatatable({
  guidelines,
  isLoading = false,
  sortBy,
  sortDirection,
  selectedCategories,
  onSort,
  onCategoryFilterChange,
  onEdit,
  onDelete,
  canManage,
  emptyTitle,
  emptyDescription,
  isFiltering = false,
}: GuidelinesDatatableProps) {
  const { t } = useTranslation('knowledge');

  const handleSort = useCallback(
    (column: SortableGuidelineKeys) => {
      // eslint-disable-next-line i18next/no-literal-string -- sort direction state values
      const nextDirection = sortBy === column && sortDirection === 'asc' ? 'desc' : 'asc';
      onSort(column, nextDirection);
    },
    [onSort, sortBy, sortDirection],
  );

  const isEmpty = !isLoading && guidelines.length === 0;
  if (isEmpty && !isFiltering) {
    return (
      <EmptyState
        title={emptyTitle ?? t('guidelines.emptyGuidelinesList.noGuidelineAddedYet')}
        description={emptyDescription ?? t('guidelines.emptyGuidelinesList.clickOnAddGuidelinesTo')}
      />
    );
  }

  return (
    <Datatable className="table-fixed">
      <DatatableHeader>
        <DatatableRow>
          {GUIDELINES_COLUMNS.map((column) =>
            column.key === 'category' ? (
              <CategoryFilterHead
                key={column.key}
                label={t(column.labelKey)}
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
                selectedCategories={selectedCategories}
                onCategoryFilterChange={onCategoryFilterChange}
              />
            ) : (
              <SortableHead
                key={column.key}
                column={column.key}
                label={t(column.labelKey)}
                className={column.className}
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
            ),
          )}
          <DatatableHead className="w-[56px]" aria-sort="none">
            <span className="sr-only">{t('guidelines.guidelinesTable.actions')}</span>
          </DatatableHead>
        </DatatableRow>
      </DatatableHeader>
      <DatatableBody>
        {isLoading &&
          Array.from({ length: SKELETON_ROWS }).map((_, rowIndex) => (
            <DatatableRow key={`skeleton-${rowIndex}`}>
              {Array.from({ length: COLUMN_COUNT }).map((_, columnIndex) => (
                <DatatableCell key={`skeleton-cell-${columnIndex}`}>
                  <Skeleton className="h-4 w-3/4" />
                </DatatableCell>
              ))}
            </DatatableRow>
          ))}
        {!isLoading && isEmpty && (
          <DatatableRow>
            <DatatableCell colSpan={COLUMN_COUNT}>
              <EmptyState
                title={t('guidelines.emptyGuidelinesList.noGuidelinesMatchYourSearch')}
                description={t('guidelines.emptyGuidelinesList.tryAdjustingYourSearchOrFilters')}
              />
            </DatatableCell>
          </DatatableRow>
        )}
        {!isLoading &&
          !isEmpty &&
          guidelines.map((guideline) => (
            <GuidelineRow
              key={guideline.id}
              guideline={guideline}
              canManage={canManage}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
      </DatatableBody>
    </Datatable>
  );
}

interface GuidelineRowProps {
  guideline: GuidelineResponseDto;
  canManage: boolean;
  onEdit: (guideline: GuidelineResponseDto) => void;
  onDelete: (guideline: GuidelineResponseDto) => void;
}

function GuidelineRow({ guideline, canManage, onEdit, onDelete }: GuidelineRowProps) {
  // There is no per-guideline detail route, so "opening" a row means opening the same
  // edit sheet as the kebab's Edit action. A view-only user can't use that sheet, so the
  // row must stay a plain, non-clickable row for them rather than showing a pointer
  // cursor that leads nowhere.
  return (
    <DatatableRow interactive={canManage} onClick={canManage ? () => onEdit(guideline) : undefined}>
      <DatatableCell>
        {canManage ? (
          // A bare `onClick` on the `<tr>` is mouse-only: a row is not focusable and has no
          // activation semantics, so keyboard and screen-reader users could reach Edit only
          // through the kebab. The name becomes the row's control instead of making the row
          // itself focusable, which keeps `role="row"` intact and gives the action a real
          // name and role. `DatatableRow` draws its ring on `focus-within`, so the button
          // takes focus and the whole row is what lights up — hence `outline-none` here
          // rather than a second, inner ring.
          <button
            type="button"
            onClick={(event) => {
              // The row handles clicks too; without this the click would run `onEdit` twice.
              event.stopPropagation();
              onEdit(guideline);
            }}
            className="block w-full truncate text-left font-medium outline-none"
          >
            {guideline.name}
          </button>
        ) : (
          <span className="truncate font-medium">{guideline.name}</span>
        )}
      </DatatableCell>
      <DatatableCell>
        <GuidelineDescriptionCell text={guideline.description} />
      </DatatableCell>
      <DatatableCell>
        <CategoryBadgeCell category={guideline.category} />
      </DatatableCell>
      <DatatableCell>{formatRelativeDate(guideline.created)}</DatatableCell>
      <DatatableCell>
        <GuidelineRowActions guideline={guideline} onEdit={onEdit} onDelete={onDelete} />
      </DatatableCell>
    </DatatableRow>
  );
}

GuidelinesDatatable.displayName = 'GuidelinesDatatable';
